import React from 'react';
import { X, UserPlus, Smartphone, Share2, ShieldCheck, CheckCircle2, Building2, LayoutDashboard, Zap, TrendingUp, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

import { Logo } from './Logo';

interface HowItWorksModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function HowItWorksModal({ isOpen, onClose }: HowItWorksModalProps) {
  const [activeTab, setActiveTab] = React.useState<'user' | 'business'>('user');

  const userSteps = [
    {
      icon: UserPlus,
      title: "1. Tell us who you are",
      description: "Just a name and email so we know you're a real neighbor.",
      color: "bg-blue-50 text-blue-600"
    },
    {
      icon: Smartphone,
      title: "2. Confirm your phone",
      description: "We send a simple code to your phone. This keeps out troublemakers.",
      color: "bg-purple-50 text-purple-600"
    },
    {
      icon: Share2,
      title: "3. Help your neighbors",
      description: "Post about traffic, rain, or fuel. Your voice helps everyone!",
      color: "bg-green-50 text-green-600"
    },
    {
      icon: ShieldCheck,
      title: "4. Be a community hero",
      description: "The more people you help, the more 'Trust Points' you earn.",
      color: "bg-amber-50 text-amber-600"
    }
  ];

  const businessSteps = [
    {
      icon: Building2,
      title: "1. Show your business",
      description: "Register your station or shop so people can find you easily.",
      color: "bg-emerald-50 text-emerald-600"
    },
    {
      icon: Zap,
      title: "2. Get more visitors",
      description: "Tell people in your area that you are open and have stock.",
      color: "bg-yellow-50 text-yellow-600"
    },
    {
      icon: LayoutDashboard,
      title: "3. Easy dashboard",
      description: "One simple screen to change your prices or post news.",
      color: "bg-zinc-900 text-white"
    },
    {
      icon: TrendingUp,
      title: "4. Grow with us",
      description: "Official updates get a special badge so people trust you more.",
      color: "bg-indigo-50 text-indigo-600"
    }
  ];

  const steps = activeTab === 'user' ? userSteps : businessSteps;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
          >
            <div className="p-6 overflow-y-auto no-scrollbar">
              <div className="flex justify-between items-center mb-6">
                <Logo size="md" showSlogan={true} />
                <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-full transition-colors">
                  <X className="w-5 h-5 text-zinc-500" />
                </button>
              </div>

              <div className="text-center mb-8">
                <h2 className="text-xl font-black text-zinc-900 mb-2 italic uppercase">How Sentlo Works</h2>
                <div className="flex p-1 bg-zinc-100 rounded-2xl">
                  <button 
                    onClick={() => setActiveTab('user')}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-black uppercase transition-all ${activeTab === 'user' ? 'bg-white text-green-600 shadow-sm' : 'text-zinc-500'}`}
                  >
                    <Users className="w-3.5 h-3.5" />
                    For Neighbors
                  </button>
                  <button 
                    onClick={() => setActiveTab('business')}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-black uppercase transition-all ${activeTab === 'business' ? 'bg-white text-green-600 shadow-sm' : 'text-zinc-500'}`}
                  >
                    <Building2 className="w-3.5 h-3.5" />
                    For Business
                  </button>
                </div>
              </div>

              <div className="space-y-6">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, x: activeTab === 'user' ? -10 : 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: activeTab === 'user' ? 10 : -10 }}
                    className="space-y-6"
                  >
                    {steps.map((step, index) => (
                      <div key={index} className="flex gap-4 group">
                        <div className={`w-12 h-12 shrink-0 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110 duration-300 ${step.color}`}>
                          <step.icon className="w-6 h-6" />
                        </div>
                        <div>
                          <h3 className="font-bold text-zinc-900 text-sm mb-1">{step.title}</h3>
                          <p className="text-xs text-zinc-600 leading-relaxed">{step.description}</p>
                        </div>
                      </div>
                    ))}
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="mt-8 p-4 bg-zinc-50 rounded-2xl border border-zinc-100">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <p className="text-[10px] font-black text-zinc-900 uppercase tracking-widest">Sentlo Commitment</p>
                </div>
                <p className="text-xs text-zinc-600 italic">
                  {activeTab === 'user' 
                    ? '"Information shared on this platform is to help the community and individuals plan their journeys more effectively."'
                    : '"We empower local businesses to reach their community directly with verified, real-time information that matters."'
                  }
                </p>
              </div>

              <button
                onClick={onClose}
                className="w-full mt-8 bg-green-600 text-white py-4 rounded-xl font-black uppercase tracking-widest hover:bg-green-700 transition-all shadow-lg shadow-green-600/20 active:scale-95"
              >
                Got it, let's go!
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
